import React, { useCallback, useContext, useEffect, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import orderBy from 'lodash/orderBy';
import { useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// HELPERS
import { RecordContext } from 'helpers/getRecordData';
import { useDetail } from '../helpers/DetailProvider';

// FUNCTIONS
import { apiFetch, modalFunctions, sleep } from 'functions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import Card from '../parts/Card';
import AMFIProductsForm from '../../../forms/amfiProducts/AMFIProducts';

// STYLES
import styles from './amfiProducts.module.scss';

// TODO: Get rid of need for this
const isTrue = v => ['true', true].includes(v);

// FORM
const AMFIProductsComponent = ({ number, onCompleted, readOnly }) => {
  const { setAMFI, setCompleted, isDisabled } = useDetail();

  const { recordID } = useParams();
  const { record: initialValues = {}, refetchRecord } = useContext(RecordContext) || {};
  const { values = {}, setFieldValue } = useFormikContext() || {};

  const initialAddAmfiProduct = useMemo(() => initialValues?.add_amfi_product, [initialValues]);

  useEffect(() => {
    setFieldValue('add_amfi_product', initialAddAmfiProduct);
    setCompleted(number, [true, false].includes(initialAddAmfiProduct));
  }, [initialAddAmfiProduct, number, setCompleted, setFieldValue]);

  const handleAdd = useCallback(
    v => {
      apiFetch({
        method: 'PUT',
        endpoint: ENDPOINTS.amFinance.products.save(recordID),
        params: {
          add_amfi_product: v
        },
        onSuccess: () => {
          const isAMFI = isTrue(v);
          setAMFI(isAMFI);
          refetchRecord({
            onComplete: () => {
              if (!isAMFI) onCompleted({ nextNumber: number + 1, delay: 500 });
            }
          });
        },
        loadingMessage: 'Saving AM/F&I Products',
        errorMessage: 'Unable to save AM/F&I Products'
      });
    },
    [number, onCompleted, setAMFI, recordID, refetchRecord]
  );

  const { add_amfi_product } = values;

  const disabled = useMemo(() => !readOnly && isDisabled(number), [isDisabled, number, readOnly]);

  const addAmfiProduct = isTrue(add_amfi_product);

  return (
    <Form.Body>
      <Form.YesNo
        name="add_amfi_product"
        label="Do you have a product or reserve to add?"
        className={clsx('mt-0', add_amfi_product && 'mb-4')}
        numeric={false}
        onChange={({ target: { value } }) => {
          if (['false', false].includes(value)) {
            modalFunctions.add({
              type: 'confirmation',
              body: 'Are you sure you do not have a product or reserve to add?',
              cancelButton: {
                onClick: () => {
                  setFieldValue('add_amfi_product', isTrue(initialAddAmfiProduct) ? true : initialAddAmfiProduct);
                },
                col: { xs: 24, sm: 12 },
                className: 'mb-4 mb-sm-0'
              },
              continueButton: {
                label: 'Confirm',
                onClick: () => handleAdd(value),
                col: { xs: 24, sm: 12 }
              },
              className: styles.modal
            });
          } else {
            handleAdd(value);
          }
        }}
        disabled={disabled}
        plaintext={readOnly}
      />
      {/* Toggle display to preserve form data */}
      <div className={clsx('w-100', !addAmfiProduct && 'd-none')}>
        <AMFIProductsForm readOnly={readOnly} />
        {!readOnly ? (
          <Form.Row className="w-100 my-2">
            <Form.Col cols={{ xs: 24, md: 5 }} />
            <Form.Col cols={{ xs: 24, md: 3 }} />
            <Form.Col cols={{ xs: 24, md: 2 }} />
            <Form.Col cols={{ xs: 24, md: 5 }} className="px-0">
              <Button.Submit variant="success" label="Save" className="w-auto" />
            </Form.Col>
          </Form.Row>
        ) : null}
      </div>
    </Form.Body>
  );
};

// MAIN COMPONENT
export default function AMFIProducts(props) {
  const { recordID } = useParams();
  const { record, refetchRecord } = useContext(RecordContext) || {};

  const { number, onCompleted, readOnly } = props;

  const ordered = useMemo(() => {
    return orderBy(
      record?.aryDealerAMFinance || [],
      ['product_type', 'product_code', 'item_name'],
      ['asc', 'asc', 'asc']
    );
  }, [record]);

  return (
    <Form
      method="PUT"
      endpoint={ENDPOINTS.amFinance.products.update(recordID)}
      initialValues={{
        add_amfi_product: record?.add_amfi_product,
        am_gross: record?.am_gross,
        fm_gross: record?.fi_gross,
        // Default certain values to empty string to render blank fields
        aryDealerAMFinance: ordered.map(o => {
          const isChecked = o.is_checked;
          const isLockCost = o.lock_cost;
          const isAMProduct = o.product_type === 0;
          const isFMProduct = o.product_type === 1;
          return {
            ...o,
            item_cost: isFMProduct || isLockCost || isChecked || o.item_cost ? o.item_cost : '',
            item_selling_price: isFMProduct || isChecked || o.item_selling_price ? o.item_selling_price : '',
            product_cost: isFMProduct || isChecked || o.product_cost ? o.product_cost : '',
            item_gross: isAMProduct || isChecked || o.item_gross ? o.item_gross : ''
          };
        })
      }}
      // TODO: For un-checked products, get API to allow an empty string for item_gross and then remove this
      formatParams={({ aryDealerAMFinance, ...params }) => ({
        ...params,
        aryDealerAMFinance: aryDealerAMFinance.map(a => {
          const isLockCost = a.lock_cost;
          const itemGross = a.item_gross || 0;
          const productCost = a.product_cost || 0;
          const itemCost = a.item_cost || 0;
          return { ...a, item_gross: itemGross, item_cost: isLockCost ? productCost : itemCost };
        })
      })}
      loadingMessage="Saving AM/F&amp;I Products"
      successMessage="AM/F&amp;I Products saved."
      errorMessage="'Unable to save AM/F&amp;I Products'"
      onSuccess={async () => {
        await sleep(500);
        refetchRecord({
          onComplete: () => onCompleted({ nextNumber: number + 1 })
        });
      }}
    >
      <Card
        number={number}
        title="AM/F&amp;I Products"
        readOnly={readOnly}
        body={<AMFIProductsComponent {...props} />}
      />
    </Form>
  );
}
